import { ContractModel, ContractCategory, ProductType } from "../../pages/contracts/models/contracts.models";

/**
 * The fixtures the dev-panel presets are built from. The default offline data and the e2e
 * expectations (card counts, dashboard totals) depend on exactly this set, so keep it stable.
 */
export const BASE_CONTRACT_FIXTURE_IDS = [
  "epk-1",
  "epk-moved",
  "otp-1",
  "kron-1",
  "epk-transit",
  "fond-1",
  "ips-1",
  "ask-1",
  "spar-1",
] as const;

/**
 * Extra products no preset uses, so the AI mock chat can build a scenario with them without
 * changing the default offline data.
 */
export const EXTENDED_CONTRACT_FIXTURE_IDS = [
  "ekstrapensjon-employer-1",
  "pkb-1",
  "fmi-1",
  "hybrid-1",
  "fripolise-1",
  "ytp-1",
  "ekstrapensjon-1",
  "livrente-1",
] as const;

/**
 * Stable ids for every offline-mode contract fixture. These strings are persisted in
 * localStorage as part of the dev mock scenario, so renaming one invalidates stored scenarios
 * (they degrade gracefully — see mock-scenario.ts).
 */
export const CONTRACT_FIXTURE_IDS = [...BASE_CONTRACT_FIXTURE_IDS, ...EXTENDED_CONTRACT_FIXTURE_IDS] as const;

export type ContractFixtureId = (typeof CONTRACT_FIXTURE_IDS)[number];

/**
 * A contract fixture is the Avtaler `ContractModel` plus whether the contract counts towards the
 * rollup totals.
 */
export interface ContractFixture {
  readonly id: ContractFixtureId;
  /** Short human label, used by the dev panel. */
  readonly label: string;
  readonly contract: ContractModel;
  /** Backend `SavingsEngagementType` name, served as `productType` on /contracts/summary. Absent for in-transit contracts. */
  readonly productType?: string;
  readonly includeInCalculation: boolean;
}

// --- Arbeidsgiver (EMPLOYER) ---

const epkContract: ContractModel = {
  id: "epk-1",
  name: "Egen pensjonskonto",
  subtitle: "Arbeidsgiver",
  detailsLink: "https://www.storebrand.no/min-side/epk",
  type: ContractCategory.Employer,
  contractOptions: { isMoved: false, isPaidUp: false },
  totalBalance: 713971,
  totalReturn: 75431,
  inTransit: false,
};

const epkMovedContract: ContractModel = {
  id: "epk-moved",
  name: "Egen pensjonskonto (flyttet)",
  subtitle: "Arbeidsgiver",
  detailsLink: null,
  type: ContractCategory.Employer,
  contractOptions: { isMoved: true, isPaidUp: false },
  totalBalance: null,
  totalReturn: null,
  inTransit: false,
};

const offentligTjenestepensjonContract: ContractModel = {
  id: "otp-1",
  name: "Offentlig tjenestepensjon",
  subtitle: "Arbeidsgiver",
  detailsLink: null,
  type: ContractCategory.Employer,
  contractOptions: { isMoved: false, isPaidUp: true },
  totalBalance: null,
  totalReturn: null,
  inTransit: false,
};

const kronContract: ContractModel = {
  id: "kron-1",
  name: "Kron pensjon",
  subtitle: "Arbeidsgiver",
  detailsLink: "https://www.storebrand.no/min-side/kron",
  type: ContractCategory.Employer,
  contractOptions: { isMoved: false, isPaidUp: false, isKron: true },
  totalBalance: 450000,
  totalReturn: null,
  inTransit: false,
};

// --- In transit (no type yet) ---

const epkInTransitContract: ContractModel = {
  id: "epk-transit",
  name: "Egen pensjonskonto på vei",
  subtitle: "Denne avtalen er på vei til Storebrand",
  detailsLink: null,
  type: null,
  contractOptions: { isMoved: false, isPaidUp: false },
  totalBalance: null,
  totalReturn: null,
  inTransit: true,
};

// --- Egen sparing (USER_SAVINGS) ---

const fundSavingContract: ContractModel = {
  id: "fond-1",
  name: "Fondssparing",
  subtitle: "Egen sparing",
  detailsLink: null,
  type: ContractCategory.UserSavings,
  contractOptions: { isMoved: false, isPaidUp: false },
  totalBalance: 125000,
  totalReturn: null,
  inTransit: false,
};

const ipsContract: ContractModel = {
  id: "ips-1",
  name: "Individuell pensjonssparing (IPS)",
  subtitle: "Egen sparing",
  detailsLink: "https://www.storebrand.no/min-side/ips",
  type: ContractCategory.UserSavings,
  contractOptions: { isMoved: false, isPaidUp: false },
  totalBalance: 50000,
  totalReturn: 2500,
  inTransit: false,
};

const askContract: ContractModel = {
  id: "ask-1",
  name: "Aksjesparekonto (ASK)",
  subtitle: "Egen sparing",
  detailsLink: null,
  type: ContractCategory.UserSavings,
  contractOptions: { isMoved: false, isPaidUp: false },
  totalBalance: null,
  totalReturn: 5000,
  inTransit: false,
};

const pensionSavingContract: ContractModel = {
  id: "spar-1",
  name: "Pensjonssparing",
  subtitle: "Egen sparing",
  detailsLink: "https://www.storebrand.no/min-side/pensjonssparing",
  type: ContractCategory.UserSavings,
  contractOptions: { isMoved: false, isPaidUp: false },
  totalBalance: 30000,
  totalReturn: null,
  inTransit: false,
};

// --- Extended fixtures: Arbeidsgiver (EMPLOYER) ---

const ekstrapensjonEmployerContract: ContractModel = {
  id: "ekstrapensjon-employer-1",
  name: "Ekstrapensjon",
  subtitle: "Arbeidsgiver",
  detailsLink: "https://www.storebrand.no/min-side/ekstrapensjon",
  type: ContractCategory.Employer,
  contractOptions: { isMoved: false, isPaidUp: false },
  totalBalance: 185000,
  totalReturn: 21000,
  inTransit: false,
};

const pkbContract: ContractModel = {
  id: "pkb-1",
  name: "Pensjonskapitalbevis",
  subtitle: "Arbeidsgiver",
  detailsLink: "https://www.storebrand.no/min-side/pkb",
  type: ContractCategory.Employer,
  contractOptions: { isMoved: false, isPaidUp: false },
  totalBalance: 240000,
  totalReturn: 18000,
  inTransit: false,
};

const fmiContract: ContractModel = {
  id: "fmi-1",
  name: "Fripolise med investeringsvalg",
  subtitle: "Arbeidsgiver",
  detailsLink: "https://www.storebrand.no/min-side/fmi",
  type: ContractCategory.Employer,
  contractOptions: { isMoved: false, isPaidUp: false },
  totalBalance: 160000,
  totalReturn: 9500,
  inTransit: false,
};

const hybridContract: ContractModel = {
  id: "hybrid-1",
  name: "Hybridpensjon med investeringsvalg",
  subtitle: "Arbeidsgiver",
  detailsLink: "https://www.storebrand.no/min-side/hybrid",
  type: ContractCategory.Employer,
  contractOptions: { isMoved: false, isPaidUp: false },
  totalBalance: 320000,
  totalReturn: 27000,
  inTransit: false,
};

// Guaranteed products have no balance, so the backend marks them paid up.
const fripoliseContract: ContractModel = {
  id: "fripolise-1",
  name: "Fripolise",
  subtitle: "Arbeidsgiver",
  detailsLink: null,
  type: ContractCategory.Employer,
  contractOptions: { isMoved: false, isPaidUp: true },
  totalBalance: null,
  totalReturn: null,
  inTransit: false,
};

const ytpContract: ContractModel = {
  id: "ytp-1",
  name: "Ytelsespensjon",
  subtitle: "Arbeidsgiver",
  detailsLink: null,
  type: ContractCategory.Employer,
  contractOptions: { isMoved: false, isPaidUp: true },
  totalBalance: null,
  totalReturn: null,
  inTransit: false,
};

// --- Extended fixtures: Egen sparing (USER_SAVINGS) ---

const ekstrapensjonContract: ContractModel = {
  id: "ekstrapensjon-1",
  name: "Ekstrapensjon",
  subtitle: "Egen sparing",
  detailsLink: "https://www.storebrand.no/min-side/ekstrapensjon",
  type: ContractCategory.UserSavings,
  contractOptions: { isMoved: false, isPaidUp: false },
  totalBalance: 95000,
  totalReturn: 8000,
  inTransit: false,
};

const livrenteContract: ContractModel = {
  id: "livrente-1",
  name: "Livrente",
  subtitle: "Egen sparing",
  detailsLink: "https://www.storebrand.no/min-side/livrente",
  type: ContractCategory.UserSavings,
  contractOptions: { isMoved: false, isPaidUp: false },
  totalBalance: 210000,
  totalReturn: 12000,
  inTransit: false,
};

export const CONTRACT_FIXTURES: Readonly<Record<ContractFixtureId, ContractFixture>> = {
  "epk-1": {
    id: "epk-1",
    label: "EPK (active)",
    contract: epkContract,
    productType: ProductType.Epk,
    includeInCalculation: true,
  },
  "epk-moved": {
    id: "epk-moved",
    label: "EPK (moved)",
    contract: epkMovedContract,
    productType: ProductType.Epk,
    includeInCalculation: true,
  },
  "otp-1": {
    id: "otp-1",
    label: "Offentlig tjenestepensjon",
    contract: offentligTjenestepensjonContract,
    productType: "PUBLIC_PENSION",
    includeInCalculation: true,
  },
  "kron-1": {
    id: "kron-1",
    label: "Kron pensjon",
    contract: kronContract,
    productType: "KRON",
    includeInCalculation: true,
  },
  "epk-transit": {
    id: "epk-transit",
    label: "EPK (in transit)",
    contract: epkInTransitContract,
    includeInCalculation: true,
  },
  "fond-1": {
    id: "fond-1",
    label: "Fondssparing",
    contract: fundSavingContract,
    productType: "FONDSKONTO_LINK",
    includeInCalculation: true,
  },
  "ips-1": { id: "ips-1", label: "IPS", contract: ipsContract, productType: "IPS", includeInCalculation: true },
  "ask-1": {
    id: "ask-1",
    label: "Aksjesparekonto",
    contract: askContract,
    productType: "ASK",
    includeInCalculation: true,
  },
  "spar-1": {
    id: "spar-1",
    label: "Pensjonssparing",
    contract: pensionSavingContract,
    productType: "IPA_LINK",
    includeInCalculation: true,
  },
  "ekstrapensjon-employer-1": {
    id: "ekstrapensjon-employer-1",
    label: "Ekstrapensjon (employer-paid)",
    contract: ekstrapensjonEmployerContract,
    productType: "EKSTRAPENSJON_EMPLOYMENT",
    includeInCalculation: true,
  },
  "pkb-1": {
    id: "pkb-1",
    label: "Pensjonskapitalbevis",
    contract: pkbContract,
    productType: ProductType.Pkb,
    includeInCalculation: true,
  },
  "fmi-1": {
    id: "fmi-1",
    label: "Fripolise med investeringsvalg",
    contract: fmiContract,
    productType: "FMI",
    includeInCalculation: true,
  },
  "hybrid-1": {
    id: "hybrid-1",
    label: "Hybridpensjon",
    contract: hybridContract,
    productType: "HYBRID_MED_INVESTERINGSVALG",
    includeInCalculation: true,
  },
  "fripolise-1": {
    id: "fripolise-1",
    label: "Fripolise",
    contract: fripoliseContract,
    productType: "FRIPOLISE",
    includeInCalculation: true,
  },
  "ytp-1": {
    id: "ytp-1",
    label: "Ytelsespensjon",
    contract: ytpContract,
    productType: "YTP",
    includeInCalculation: true,
  },
  "ekstrapensjon-1": {
    id: "ekstrapensjon-1",
    label: "Ekstrapensjon (own savings)",
    contract: ekstrapensjonContract,
    productType: "EKSTRAPENSJON",
    includeInCalculation: true,
  },
  "livrente-1": {
    id: "livrente-1",
    label: "Livrente",
    contract: livrenteContract,
    productType: "LIVRENTE_LINK",
    includeInCalculation: true,
  },
};

/** Maps fixture ids to their corresponding ContractFixture instances. */
export function getContractFixtures(ids: readonly ContractFixtureId[]): readonly ContractFixture[] {
  return ids.map((id) => CONTRACT_FIXTURES[id]);
}
